import React from 'react'
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material'
import { CloudUpload, Add } from '@mui/icons-material'
import { TrashIcon, UploadDocumentIcon } from '../../icons'
import { fileUpload, fileDelete } from '../../api/uploadApi'
import ValidationErrorText from '../common/ValidationErrorText'

const UploadRelevantDocument = ({ label, value = [], onChange, error = '', setError = () => { }, readOnly = false,fieldErrors,fieldType }) => {
  const docs = value

  const handleFileUpload = async (file, index) => {
    setError('')
    onChange(docs.map((doc, i) =>
      i === index ? { ...doc, uploading: true } : doc
    ))

    try {
      const data = await fileUpload(file)
      onChange(docs.map((doc, i) =>
        i === index
          ? { ...doc, file, url: data.url || '', uploading: false }
          : doc
      ))
    } catch (e) {
      setError('File upload failed!')
      onChange(docs.map((doc, i) =>
        i === index ? { ...doc, uploading: false } : doc
      ))
    }
  }

  const handleTitleChange = (index, value) => {
    onChange(docs.map((doc, i) =>
      i === index ? { ...doc, title: value } : doc
    ))
  }

  const handleFileChange = (index, file) => {
    if (!file) return
    handleFileUpload(file, index)
  }

  const handleRemove = (index) => {
    onChange(docs.filter((_, i) => i !== index))
  }

  const handleRemoveFile = async (index) => {
    const fileUrl = docs[index].url
    try {
      if (fileUrl) {
        await fileDelete(fileUrl)
      }
    } catch (err) {
      console.error('Failed to delete file from server:', err)
      setError('Failed to delete file from server')
      return
    }

    onChange(docs.map((doc, i) =>
      i === index ? { ...doc, file: null, url: '' } : doc
    ))
  }

  const handleAddMore = () => {
    onChange([...docs, { title: '', file: null, uploading: false, url: '' }])
  }

  const inputStyle = {
    height: 38,
    fontSize: 13,
    '& input': {
      height: '100%',
      padding: '0 14px',
      fontSize: 13,
    },
  }

  return (
    <Box mt={4}>
      <Typography fontSize="14px" fontWeight="500" mb={1}>
        {label}
      </Typography>
      {error && (
        <Typography color="error" fontSize={13} mb={1}>{error}</Typography>
      )}
      {docs.map((doc, index) => (
        <Box key={index} mb={3}>
          <Box display="flex" alignItems="center" justifyContent={"center"} gap={1}>
            <Box width={"100%"}>
            <TextField
              fullWidth
              size="small"
              placeholder="For example, additional information or guidelines."
              value={doc.title}
              onChange={(e) => !readOnly && handleTitleChange(index, e.target.value)}
              InputProps={{ sx: inputStyle, readOnly }}
              disabled={readOnly}
            />
            <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].title`]}/>
            </Box>
            {!readOnly && (
              <IconButton onClick={() => handleRemove(index)}>
                <TrashIcon />
              </IconButton>
            )}
          </Box>

          {doc.uploading ? (
            <Box mt={2} mb={1} display="flex" alignItems="center" gap={2}>
              <CircularProgress size={24} />
              <Typography fontSize="14px">Uploading...</Typography>
            </Box>
          ) : (doc.file || doc.url) ? (
            <Box
              sx={{
                border: '1px solid #ccc',
                p: 2,
                maxWidth: '300px',
                borderRadius: 2,
                mt: 2,
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <UploadDocumentIcon color="action" style={{ minWidth: '22px', width: '22px' }} />
                <Typography fontSize="14px" sx={{ width: '100%' }}>
                  {doc.file?.name || doc.title || 'Uploaded File'}
                  {doc.file?.size && (
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        display: 'block',
                      }}
                    >
                      ({Math.round(doc.file.size / 1024)} KB)
                    </span>
                  )}
                  {doc.url && (
                    <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: 11 }}>
                      View Uploaded
                    </a>
                  )}
                </Typography>
                {!readOnly && (
                  <IconButton size="small" onClick={() => handleRemoveFile(index)}>
                    <TrashIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          ) : null}

          {!readOnly && (
            <>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ color: '#1075FF', border: '1px solid #1075FF', textTransform: 'capitalize', px: 3, mt: 1 }}
                disabled={doc.uploading}
              >
                {(doc.url && !doc.file) ? 'Replace Attachment' : doc.file ? 'Re-upload Attachment' : 'Upload Attachment'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.docx,.txt,.png,.jpg,.jpeg"
                  onChange={(e) => handleFileChange(index, e.target.files[0])}
                />
              </Button>
              <Typography variant="caption" display="block" mt={1}>
                Upload a clear PDF / PNG / DOCX / TXT, Max 50MB
              </Typography>
            <ValidationErrorText error={fieldErrors?.[`${fieldType}[${index}].url`]}/>

            </>
          )}
        </Box>
      ))}

      {!readOnly && (
        <Button
          startIcon={<Add />}
          variant="outlined"
          onClick={handleAddMore}
          sx={{ color: '#1075FF', border: '1px solid #1075FF', textTransform: 'capitalize', px: 3, mt: 1 }}
        >
          Add More
        </Button>
      )}
    </Box>
  )
}

export default UploadRelevantDocument
